const ACCEPTED_IMAGE_TYPES = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
};

function normalizeMimeType(mimeType) {
  return (mimeType || "").split(";")[0].trim().toLowerCase();
}

function shouldFallbackToPng(mimeType) {
  const normalized = normalizeMimeType(mimeType);
  return !normalized || normalized === "application/octet-stream";
}

function getImageFileMetadata(mimeType) {
  const normalized = normalizeMimeType(mimeType);

  if (ACCEPTED_IMAGE_TYPES[normalized]) {
    return {
      mimeType: normalized === "image/jpg" ? "image/jpeg" : normalized,
      extension: ACCEPTED_IMAGE_TYPES[normalized],
    };
  }

  return {
    mimeType: "image/png",
    extension: "png",
  };
}

function validateGeneratedImageBlob(blob, maxSizeMb) {
  const normalized = normalizeMimeType(blob && blob.type);

  if (!ACCEPTED_IMAGE_TYPES[normalized] && !shouldFallbackToPng(normalized)) {
    throw new Error("invalid_format");
  }

  if (blob && blob.size > maxSizeMb * 1024 * 1024) {
    throw new Error("size_exceeded");
  }
}

function getFirstGeneratedImage(responseData) {
  const images = responseData && Array.isArray(responseData.data) ? responseData.data : [];
  return images[0] || null;
}

function getGeneratedImagePreviewSource(image) {
  if (!image) return "";
  if (image.b64_json) return `data:image/png;base64,${image.b64_json}`;
  return image.url || image.image_url || "";
}

function base64ToBlob(base64, mimeType) {
  const byteCharacters = typeof atob === "function"
    ? atob(base64)
    : Buffer.from(base64, "base64").toString("binary");
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);

    for (let i = 0; i < slice.length; i += 1) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    byteArrays.push(new Uint8Array(byteNumbers));
  }

  return new Blob(byteArrays, { type: mimeType });
}

async function createGeneratedImageFile(image, options) {
  const maxSizeMb = options.maxSizeMb;
  const filePrefix = options.filePrefix || "ai-cover";
  const fetcher = options.fetcher || fetch;
  let blob;
  let metadata;

  if (image && image.b64_json) {
    metadata = getImageFileMetadata(image.mime_type || "image/png");
    blob = base64ToBlob(image.b64_json, metadata.mimeType);
  } else {
    const imageUrl = image && (image.url || image.image_url);
    if (!imageUrl) throw new Error("missing_image");

    const response = await fetcher(imageUrl);
    if (!response.ok) throw new Error("download_failed");

    blob = await response.blob();
    validateGeneratedImageBlob(blob, maxSizeMb);
    metadata = getImageFileMetadata(blob.type);
  }

  validateGeneratedImageBlob(blob, maxSizeMb);

  return new File([blob], `${filePrefix}.${metadata.extension}`, {
    type: metadata.mimeType,
  });
}

module.exports = {
  getImageFileMetadata,
  validateGeneratedImageBlob,
  getFirstGeneratedImage,
  getGeneratedImagePreviewSource,
  createGeneratedImageFile,
};
